import { appstr } from "../utils/appstr";
import { ConvertToLockFlag } from "../utils/ConvertToLockFlag";
import { CPCompare } from "../utils/CPCompare";
import { Div2Floor } from "../utils/Div2Floor";
import {
  enumBoxDragMode,
  enumBoxOverlay,
  enumBoxShapes,
  enumBoxSubTypes,
  enumBoxType,
  enumBranchStyle,
  enumChartBoxInteractionMode,
  enumChartElement,
  enumChartRenderOrder,
  enumChartTypes,
  enumContainerType,
  enumDashStyle,
  enumDropType,
  enumEmptyPositionLayoutType,
  enumElementBorderStyle,
  enumElementDrawStates,
  enumElementLayer,
  enumFillMode,
  enumFlagType,
  enumHorzCentering,
  enumOverrideType,
  enumPanelMode,
  enumPersistStatus,
  enumScalingMethod,
  enumSubChartFlag,
  enumUIModes,
  enumUpdateProperty,
  enumVertCentering,
  gBrightnessThreshold,
  kBackspaceKey,
  kBoxShadowOffset,
  kDeleteKey,
  kDoubleBorderWidthAdder,
  kMiniTriWidth,
  kReturnKey,
  kRoundRectRadius,
  kTabKey,
  kTriAnnoW
} from "../constants";
import { GetLaunchHyperlink } from "../utils/GetLaunchHyperlink";
import { GetSCArrowHeight } from "../utils/GetSCArrowHeight";
import { isInPlanning } from "../utils/isInPlanning";
import { PositionLocked } from "../utils/PositionLocked";
import { QBoxDraggerPanel } from "./QBoxDraggerPanel";
import { QBoxOverlay_Add } from "./QBoxOverlay_Add";
import { QBoxOverlay_Change } from "./QBoxOverlay_Change";
import { QBoxOverlay_Chat } from "./QBoxOverlay_Chat";
import { QBoxOverlay_MoveOut } from "./QBoxOverlay_MoveOut";
import { QBoxOverlay_Wand } from "./QBoxOverlay_Wand";
import { QBoxPageLink } from "./QBoxPageLink";
import { QBoxResizer } from "./QBoxResizer";
import { QChartBoxCell } from "./QChartBoxCell";
import { QConnectingLineGroup } from "./QConnectingLineGroup";
import { QElement } from "./QElement";
import { QRect } from "./QRect";
import { QRoundRect } from "./QRoundRect";
import { QSubChartArrow } from "./QSubChartArrow";
import { SVG_RenderAnno } from "../utils/SVG_RenderAnno";
import { ThicknessAdjuster } from "../utils/ThicknessAdjuster";
import { XML_RenderAnno } from "../utils/XML_RenderAnno";
import { Globals } from "./Globals";
import { XML_RenderChartBox } from "../utils/XML_RenderChartBox";
import { SVG_RenderChartBox } from "../utils/SVG_RenderChartBox";
import { QBoxOverlay_Move } from "./QBoxOverlay_Move";
import { ContentLocked } from "../utils/ContentLocked";
import { DetermineBrightness } from "../utils/DetermineBrightness";
import { SetCtxFont } from "../utils/SetCtxFont";
import { DrawBSpacer } from "../utils/DrawBSpacer";
import { QBoxOverlay_Delete } from "./QBoxOverlay_Delete";
import { QBoxOverlay_Lock } from "./QBoxOverlay_Lock";
import { QBoxOverlay } from "./QBoxOverlay";
import { QBoxOverlay_Filter } from "./QBoxOverlay_Filter";

/**
 * @typedef {import('./QCanvasPanel').QCanvasPanel} QCanvasPanel
 * @typedef {import('./QObjDBObject').QObjDBObject} QObjDBObject
 * @typedef {import('./QXWSMgr').QXWSMgr} QXWSMgr
 */

export class QChartBox extends QElement {
  constructor() {
    super();
    this.fWSMgr = null;
    this.fLayout = null;
    /**
     *  @type {(QCanvasPanel | null)}
     */
    this.fParentPanel = null;
    this.fLayoutCells = [];

    this.fBoxMode = null;

    /**
     * @type {(QObjDBObject | null)}
     */
    this.fDataObj = null;
    this.fDataObjID = null;

    this.fPositionDataObj = null;

    this.fConnectingLineGroup = null;

    this.fBoxResizer = null;
    this.fSubChartDrill = null;
    this.fAutoTextRef = null;

    this.fObjectName = "QChartBox";
    this.fElements = null;
    this.fPlanOverrideType = null;
  }

  InitChartBox(
    layout,
    parentPanel,
    xPos,
    yPos,
    boxMode,
    dataObj,
    dataObjID,
    restoreCellSelection,
    wsMgr,
    lightInit,
    suppressOverlays = false
  ) {
    this.fWSMgr = wsMgr;
    this.fBoxMode = boxMode;
    this.fDataObj = dataObj;
    this.fDataObjID = dataObjID;
    this.fParentPanel = parentPanel;

    var boxType = layout.GetBoxType();
    var chartElementType = enumChartElement.chartBox;
    var renderOrder = enumChartRenderOrder.chartBox;
    if (boxType == enumBoxType.nonstandard) {
      var subType = layout.GetBoxSubType();
      switch (subType) {
        case enumBoxSubTypes.refMRBox:
          chartElementType = enumChartElement.chartBox;
          renderOrder = enumChartRenderOrder.chartBoxGroups;
          break;
        default:
          chartElementType = enumChartElement.textBox;
          //renderOrder = enumChartRenderOrder.textBox;
          break;
      }
    }

    var boxLayer = layout.GetLayer();
    this.SetLayer(boxLayer);

    this.SetElementType(chartElementType);
    this.SetZPlane(renderOrder);

    layout.SetDBObj(dataObj);
    // We also use the ID for MRB where it matters to keep it updated during runtime
    layout.SetDBObjID(dataObjID);

    layout.SetPosition(xPos, yPos);
    this.SetPositionX(xPos);
    this.SetPositionY(yPos);

    parentPanel.PushElement(this);

    this.fLayout = layout;
    if (lightInit == false) {
      this.fLayoutCells = [];

      var subChartFlag = layout.GetSubChartFlag();
      //TODO: Rasool- the container itself should carry this flag. I ask both the root
      if (
        subChartFlag !== enumSubChartFlag.drilldown ||
        ((layout.GetContainerObjID() || layout.GetContainerBottomLinesToColumns()) &&
          subChartFlag === enumSubChartFlag.drilldown)
      ) {
        this.fConnectingLineGroup = new QConnectingLineGroup();
        this.fConnectingLineGroup.InitConnectingLineGroup(layout, dataObj, parentPanel);
      }

      this.ResetBoxLayout(restoreCellSelection);
    }

    var roMode = Globals._accountInfo.ReadOnlyMode() || layout.GetBoxStyleID() == -1;
    var boxSelectionDisabled =
      layout.GetBoxStyleID() == -1 || layout.GetBranchStyle() == enumBranchStyle.index;
    var addResizer = !roMode && !this.IsAutoText() && !this.IsMRB();
    var addPageLink = boxSelectionDisabled && layout.GetContainerType() == enumContainerType.nonmrb;

    if (addResizer) this.AddBoxResizer();

    if (this.IsStaticPicture() && !roMode) {
      var imageCmd = Globals._uiConfig.InBackgroundEditorMode()
        ? "UploadNewFile"
        : "ImageProperties";
      this.SetDblClickCmd(imageCmd);
    }

    this.SetIsSelectable(!boxSelectionDisabled); // for disabling events e.g mouse clicks, drags, selections, etc.
    this.SetEnabled(!boxSelectionDisabled); // for rendering purposes. e.g. highlighting, etc.
    if (!boxSelectionDisabled) {
      this.SetDraggable(true);
    }
    if (addPageLink) this.AddPageLink();

    if (dataObj && !suppressOverlays) {
      if (this.CheckShowPlanIcons(wsMgr)) {
        var overlayType = wsMgr.fPlanManager.AssignIconToBox(dataObj);
        if (overlayType != null) this.AddElement(overlayType);
      } else if (dataObj.IsMuted() || dataObj.IsFiltered()) {
        // mute flag to supersede filter flag
        this.AddElement(enumBoxOverlay.filter);
      } else if (layout.HasOverrides() && !Globals._accountInfo.ReadOnlyMode())
        this.AddElement(enumBoxOverlay.wand);
    }

    this.AddSubChartDrill();
  }

  /**
   *
   * @param {QXWSMgr} wsMgr
   * @returns
   */
  CheckShowPlanIcons(wsMgr) {
    if (!wsMgr.fPlanManager) {
      return false;
    }

    if (Globals._publishRef) {
      return Globals._pubSettings.fShowOverlay;
    }

    return Globals._uiConfig.fShowOverlay;
  }

  GetDragPanelClass() {
    return new QBoxDraggerPanel();
  }

  DoCloseElement() {
    this.fClosed = true;
    this.fDataObj = null;
    this.fLayout = null;
    this.fLayoutCells = [];
    this.fConnectingLineGroup = null;
    this.fBoxResizer = null;
    this.fElements = null;
    this.fSubChartDrill = null;
    this.fAutoTextRef = null;
  }

  //==========================================================================================
  // Auto Text
  SetAutoTextRef(refCell) {
    this.fAutoTextRef = refCell;
  }

  IsAutoText() {
    var boxSubType = this.GetBoxSubType();
    return boxSubType == enumBoxSubTypes.autoText;
  }

  IsMRB() {
    var boxSubType = this.GetBoxSubType();
    return boxSubType == enumBoxSubTypes.refMRBox;
  }

  IsPositionBox() {
    var dataObj = this.GetDataObj();
    if (!dataObj) return false;
    return dataObj.IsPositionObj();
  }

  IsStaticPicture() {
    var boxSubType = this.GetBoxSubType();
    return boxSubType == enumBoxSubTypes.picture;
  }

  IsVacantBox() {
    return this.GetLayout()?.fBoxContentSubType === enumEmptyPositionLayoutType.vacant;
  }

  GetSPictureScaleH() {
    if (!this.IsStaticPicture()) return 0;

    var l = this.GetLayout();
    var c1 = l.GetCellDesc(1, 1);
    return c1.GetSPictureScaleH();
  }

  GetSPictureScaleW() {
    if (!this.IsStaticPicture()) return 0;

    var l = this.GetLayout();
    var c1 = l.GetCellDesc(1, 1);
    return c1.GetSPictureScaleW();
  }

  CanBeGrouped() {
    var curContainer = this.GetContainerNode();
    if (!this.GetDataObj()) {
      console.log("invalid");
      return false;
    } else if (
      !this.GetDataObj().GetParent() &&
      curContainer &&
      curContainer.GetAssociatedNodes().length > 0
    )
      return true;
    return false;
  }

  GetScalingMethod() {
    var l = this.GetLayout();
    var c1 = l.GetCellDesc(1, 1);
    return c1.GetScalingMethod();
  }

  SetScalingMethod(scalingMethod) {
    var l = this.GetLayout();
    var c1 = l.GetCellDesc(1, 1);
    var br = this.fBoxResizer;

    if (br != null) {
      var allowDeltaX =
        scalingMethod != enumScalingMethod.scaletopage &&
        scalingMethod != enumScalingMethod.scalehorz;
      var allowDeltaY =
        scalingMethod != enumScalingMethod.scaletopage &&
        scalingMethod != enumScalingMethod.scalevert;

      br.SetAllowDeltaX(allowDeltaX);
      br.SetAllowDeltaY(allowDeltaY);

      br.SetKeepAspectRatio(scalingMethod == enumScalingMethod.lockaspect);
    }

    c1.SetScalingMethod(scalingMethod);
  }

  SetSPictureScale(scaleW, scaleH, scalingMethod, userUpdate = false) {
    if (!this.IsStaticPicture()) return;

    var parent = this.fParentPanel;
    var w = parent.fWSMgr;

    var p = this.fParentPanel.CalculateScale(scaleW, scaleH, scalingMethod, this);
    scaleW = p.fX;
    scaleH = p.fY;

    this.SetScalingMethod(scalingMethod);
    var l = this.GetLayout();
    var c1 = l.GetCellDesc(1, 1);
    var p = c1.ScaleSPicture(scaleW, scaleH);

    l.SetBoxWidth(p.fX);
    l.SetFixedHeight(p.fY);

    l.CalculateLayout();

    if (Globals._uiConfig.InBackgroundEditorMode() && userUpdate) {
      w.fCanvasPanel?.UpdateLayoutBackgroundMgr(l);
    }

    this.ResetBoxLayout(true);
  }

  GetSPictureDirectory() {
    // if (!this.IsStaticPicture())
    //      return 0;

    var l = this.GetLayout();
    var c1 = l.GetCellDesc(1, 1);
    return c1.GetSPictureDirectory();
  }

  GetSPictureFile() {
    // if (!this.IsStaticPicture())
    //      return 0;

    var l = this.GetLayout();
    var c1 = l.GetCellDesc(1, 1);
    return c1.GetSPictureFile();
  }

  GetAutoTextType() {
    var l = this.GetLayout();
    var c1 = l.GetCellDesc(1, 1);
    var a = c1.GetAutoTextType();
    return a;
  }

  GetDoc() {
    const parent = this.fParentPanel;
    return parent?.fDoc;
  }

  ImageLoaded(pRef, redraw) {
    var parent = this.fParentPanel;
    var w = parent.fWSMgr;

    var theLayout = this.fLayout;
    var theDoc = parent.fDoc;
    var theImage = theDoc.FindPicture(pRef.fDirectory, pRef.fFileName);
    var oWidth = theImage.width;
    var oHeight = theImage.height;

    var iHeight = oHeight;

    var c1 = theLayout.GetCellDesc(1, 1);

    c1.SetSPictureDirectory(pRef.fDirectory);
    c1.SetSPictureFile(pRef.fFileName);
    c1.SetCellHeight(iHeight);
    c1.SetSPictureSize(oWidth, oHeight);

    theLayout.SetFixedHeight(iHeight);
    theLayout.SetBoxWidth(oWidth);
    theLayout.CalculateLayout();

    this.ResetBoxLayout(true);

    w.SetToolMode("ToolMode3", false);
    parent.DoSelectElement(this, true);

    if (this.GetLayer() === enumElementLayer.master && Globals._uiConfig.InBackgroundEditorMode())
      w.fCanvasPanel?.UpdateLayoutBackgroundMgr(theLayout);

    if (redraw) {
      w.PrepRenderWS();
      w.RenderWS();
    }
  }

  PrepRender() {
    if (this.fAutoTextRef) {
      var changed = this.fAutoTextRef.CacheAutoText();
      if (changed) {
        this.ForceLayoutRecalc(true); // ,null);
        //	var l = this.GetLayout();
        //	var hCentering = l.GetChartHorzCentering();
        //	var vCentering = l.GetChartVertCentering();
        //Note- shouldn't do this random extra call...
        this.fParentPanel.PositionTextBox(this, null, null);
      }
    }
  }

  //==========================================================================================
  SetEnableExpander(enable) {
    if (enable) {
      if (this.fExpander == null) this.AddExpander();
    } else {
      if (this.fExpander != null) this.RemoveExpander();
    }
  }

  SetEnableResizer(enable) {
    if (enable) {
      if (this.fBoxResizer == null) this.AddBoxResizer();
    } else {
      if (this.fBoxResizer != null) this.RemoveBoxResizer();
    }
  }

  AddBoxResizer() {
    var isPicture = this.IsStaticPicture();
    //var scalingMethod = this.GetScalingMethod();
    //var lockAspectRatio = isPicture ? scalingMethod == enumScalingMethod.lockaspect : false;
    this.fBoxResizer = new QBoxResizer();
    this.fBoxResizer.InitBoxResizer(this, true, isPicture, isPicture);
    this.fParentPanel.PushElement(this.fBoxResizer);
  }

  AddElement(elementType) {
    var element = new QBoxOverlay_Add();
    switch (elementType) {
      case enumBoxOverlay.add:
        element = new QBoxOverlay_Add();
        element.Init(this.fWSMgr);
        break;
      case enumBoxOverlay.move:
        element = new QBoxOverlay_Move();
        element.Init(this.fWSMgr);
        break;
      case enumBoxOverlay.moveout:
        element = new QBoxOverlay_MoveOut();
        element.Init(this.fWSMgr);
        break;
      case enumBoxOverlay.delete:
        element = new QBoxOverlay_Delete();
        element.Init(this.fWSMgr);
        break;
      case enumBoxOverlay.change:
        element = new QBoxOverlay_Change();
        element.Init(this.fWSMgr);
        break;
      case enumBoxOverlay.lock:
        element = new QBoxOverlay_Lock();
        element.Init(this.fWSMgr);
        break;
      case enumBoxOverlay.comment:
        element = new QBoxOverlay_Chat();
        element.Init(this.fWSMgr);
        break;
      case enumBoxOverlay.mute: // intentional fallthrough
      case enumBoxOverlay.filter:
        element = new QBoxOverlay_Filter();
        element.Init(this.fWSMgr, elementType === enumBoxOverlay.filter);
        break;
      case enumBoxOverlay.wand:
        element = new QBoxOverlay_Wand();
        element.Init(this.fWSMgr, elementType === enumBoxOverlay.filter);
        break;
    }

    element.InitBoxOverlay(this);

    this.PushElement(element);
    //TODO: revisit this for chartbox-overlay overlap issue
    //this.fParentPanel.PushElement(element);
    return element;
  }

  RemoveOverlay(subType) {
    if (!subType) subType = QBoxOverlay;
    for (var anElemIdx in this.fElements) {
      var theElement = this.fElements[anElemIdx];
      //TODO: subtype logic
      if (theElement instanceof QBoxOverlay) {
        this.RemoveElement(theElement);
        return;
      }
    }
  }

  RemoveConnectingLines() {
    if (this.fConnectingLineGroup) this.fConnectingLineGroup.ClearGroup(this.fParentPanel);
  }

  RemoveBoxResizer() {
    this.fParentPanel.RemoveElement(this.fBoxResizer);
  }

  RemoveLayoutCells() {
    var layoutCellSet = new Set(this.fLayoutCells);
    this.fFilteredElDict = { first: [], last: [] };
    if (this.fElements != null) {
      var lastIdx = this.fElements.length - 1;
      this.fFilteredElements = this.fElements.filter(function (anElement) {
        return !layoutCellSet.has(anElement);
      });
      for (var i = 0; i < this.fFilteredElements.length; i++) {
        if (this.fElements.indexOf(this.fFilteredElements[i]) == lastIdx)
          this.fFilteredElDict["last"].push(this.fFilteredElements[i]);
        else this.fFilteredElDict["first"].push(this.fFilteredElements[i]);
      }
      this.fElements = [];
    }
    this.fLayoutCells = [];
  }

  AddConnectingLines(pageMgr) {
    if (this.fConnectingLineGroup)
      this.fConnectingLineGroup.InitConnectingLines(pageMgr, this["nodeRef"]);
  }

  SaveCellSelection(theLayout) {
    var numCells = theLayout.GetNumCells();
    for (var i = 0; i < numCells; i++) {
      var cellDesc = theLayout.GetIndexCellDesc(i);
      cellDesc["SaveSel"] = false;
    }

    if (this.fLayoutCells == null) return;

    var numElements = this.fLayoutCells.length;
    for (var i = 0; i < numElements; i++) {
      var theElement = this.fLayoutCells[i];

      if (theElement != this) theElement["invalid"] = true;
      if (theElement.IsSelected() && theElement.IsShown()) {
        var cellDesc = theElement.GetCellDesc();
        cellDesc["SaveSel"] = true;
      }
    }
  }

  GetPreviewMode() {
    var parentPanel = this.fParentPanel;
    var previewMode = parentPanel.GetPreviewMode();
    return previewMode;
  }

  InitLayoutCells(restoreCellSelection) {
    var xPos = this.fLayout.GetPositionX();
    var yPos = this.fLayout.GetPositionY();
    this.fHorzOffset = xPos;
    this.fVertOffset = yPos;

    if (this.fLayoutCells == null) return;

    var parentPanel = this.fParentPanel;
    var curBoxMode = this.GetBoxMode();
    this.RemoveLayoutCells();
    var theLayout = this.fLayout;

    var numCells = theLayout.GetNumCells();
    for (var i = 0; i < numCells; i++) {
      var cellDesc = theLayout.GetIndexCellDesc(i);

      var isShown = !cellDesc.IsHidden();
      if (isShown) {
        var aCell = new QChartBoxCell();
        aCell.InitLayoutCell(cellDesc, theLayout, this, this.GetDataObj());
        aCell.SetCellMode(curBoxMode);

        if (cellDesc.IsGutterCell()) aCell.SetElementType(enumChartElement.gutterCell);
        else aCell.SetElementType(enumChartElement.boxCell);

        var x = xPos + cellDesc.GetLocationX();
        var y = yPos + cellDesc.GetLocationY();

        var fillColor = cellDesc.GetCellFillColor();
        var fillColor2 = cellDesc.GetCellFillColor2();
        var fillMode = cellDesc.GetCellFillMode();

        aCell.SetFillColor(fillColor);
        aCell.SetFillColor2(fillColor2);

        aCell.SetFillMode(fillMode);
        aCell.PositionElement(x, y);
        if (aCell.IsObject()) aCell.PositionElements();

        this.fLayoutCells.push(aCell);
        this.PushElement(aCell);

        if (restoreCellSelection) {
          var wasSelected = cellDesc["SaveSel"];
          if (wasSelected) {
            //console.log("Restoring Selection for ", aCell.TEGetContentToRender())

            parentPanel.DoSelectElement(aCell, true);
          }
          //aCell.SetSelected(true); //Doublecheck
        }
      } else {
        var h = 1;
      }
    }
    this.fElements = this.fFilteredElDict["first"]
      .concat(this.fElements)
      .concat(this.fFilteredElDict["last"]);
  }

  //=================================================================================================
  // Box Mode
  GetBoxMode() {
    return this.fBoxMode;
  }

  // Get's called when going from marque<->layout modes
  SetBoxMode(boxMode) {
    this.fBoxMode = boxMode;
    boxMode = this.GetBoxMode();
    var numCells = this.fLayoutCells.length;
    for (var i = 0; i < numCells; i++) {
      var aCell = this.fLayoutCells[i];
      if (aCell != this) aCell.SetCellMode(boxMode);
    }
  }

  SetBoxStylePage(thePage) {
    this["page"] = thePage;
    var numCells = this.fLayoutCells.length;
    for (var i = 0; i < numCells; i++) {
      var aCell = this.fLayoutCells[i];
      aCell["page"] = thePage;
    }
  }

  ResetBoxLayout(restoreCellSelection) {
    //console.log("ResetBoxLayout");
    this.fLayout.fCachedHeight = null;
    if (this.fLayout?.fContainerEnvelope) this.fLayout.fContainerEnvelope = null;
    this.fLayout.CalculateLayout();
    if (this.fConnectingLineGroup) this.fConnectingLineGroup.SetLayout(this.fLayout);
    this.InitLayoutCells(restoreCellSelection);
  }

  GetLayout() {
    return this.fLayout;
  }

  // Box Fill Mode
  SetBoxFillMode(fillMode) {
    this.fLayout.SetBoxFillMode(fillMode);
  }

  GetBoxFillMode() {
    return this.fLayout.GetBoxFillMode();
  }

  SetOverrideBoxFillMode(fillMode) {
    return this.fLayout.SetOverrideBoxFillMode(fillMode);
  }

  // Box Fill Color
  SetBoxFillColor(fillColor) {
    this.fLayout.SetBoxFillColor(fillColor);
  }

  GetBoxFillColor() {
    return this.fLayout.GetBoxFillColor();
  }

  SetOverrideBoxFillColor(fillColor) {
    this.fLayout.SetOverrideBoxFillColor(fillColor);
  }

  // Box Fill Color Gradient
  SetBoxFillColorGradient(fillColor2) {
    this.fLayout.SetBoxFillColorGradient(fillColor2);
  }

  SetOverrideBoxFillColorGradient(fillColorGradient) {
    this.fLayout.SetOverrideBoxFillColorGradient(fillColorGradient);
  }

  GetBoxFillColorGradient() {
    return this.fLayout.GetBoxFillColorGradient();
  }

  // Box Border Color
  SetOverrideBoxBorderColor(borderColor) {
    this.fLayout.SetOverrideBoxBorderColor(borderColor);
  }

  SetBoxBorderColor(borderColor) {
    this.fLayout.SetBoxBorderColor(borderColor);
  }

  GetBoxBorderColor() {
    return this.fLayout.GetBoxBorderColor();
  }

  // Box Shape
  SetOverrideBoxShapeType(boxShape) {
    this.fLayout.SetOverrideBoxShapeType(boxShape);
  }

  SetBoxShapeType(boxShape) {
    this.fLayout.SetBoxShapeType(boxShape);
  }

  GetBoxShapeType() {
    return this.fLayout.GetBoxShapeType();
  }

  // Border Thickness
  SetBorderThickness(thickness) {
    this.fLayout.SetBorderThickness(thickness);
  }

  GetBorderThickness() {
    return this.fLayout.GetBorderThickness();
  }

  SetOverrideBorderThickness(thickness) {
    this.fLayout.SetOverrideBorderThickness(thickness);
  }

  // Border Style
  SetBorderStyle(borderStyle) {
    this.fLayout.SetBorderStyle(borderStyle);
  }

  GetBorderStyle() {
    return this.fLayout.GetBorderStyle();
  }

  SetOverrideBorderStyle(borderStyle) {
    this.fLayout.SetOverrideBorderStyle(borderStyle);
  }

  // Dash Style
  SetDashStyle(dashStyle) {
    this.fLayout.SetDashStyle(dashStyle);
  }

  GetDashStyle() {
    return this.fLayout.GetDashStyle();
  }

  SetOverrideDashStyle(dashStyle) {
    this.fLayout.SetOverrideDashStyle(dashStyle);
  }

  SetWidth(theWidth) {
    //	this.fLayout.SetBoxWidth(theWidth);
  }

  GetWidth() {
    return this.fLayout.GetBoxWidth();
  }

  SetHeight(theHeight) {
    //this.fLayout.SetBoxHeight(theHeight);
  }

  GetHeight() {
    return this.fLayout.GetBoxHeight(false);
  }

  GetSubChartFlag() {
    return this.fLayout.GetSubChartFlag();
  }

  SetSubChartFlag(flag) {
    this.fLayout.SetSubChartFlag(flag);
  }

  BottomOfSubChartBreak(checkTopOfChart) {
    var sc = this.fLayout.GetSubChartFlag();
    if (sc == enumSubChartFlag.drillup) return true;

    if (checkTopOfChart) {
      var dbObj = this.GetDataObj();
      if (dbObj == null) return true;

      var parent = dbObj.GetParent();
      if (parent == null) {
        //New check for cohead situation.
        if (this.GetContainerNode()) {
          var associatedNodes = this.GetContainerNode().GetAssociatedNodes();
          //We allow moving a branch within a cohead group as long as there is a still a head of chart.
          if (associatedNodes.length > 1) return false;
        }
        return true;
      }
    }

    return false;
  }

  TopOfSubChartBreak() {
    var sc = this.GetSubChartFlag();
    if (this.GetContainerNode()) sc = this.GetContainerNode().GetSubChartFlag();

    if (sc == enumSubChartFlag.drilldown) return true;

    return false;
  }

  // REDFLAG
  GetAdjBorderWidth(borderStyle) {
    var borderWidth = this.GetBorderThickness();
    var borderSize = 0;
    switch (borderStyle) {
      case enumElementBorderStyle.noBorder:
        borderSize = 0;
        break;
    }

    return borderSize;
  }

  // Shadow On
  SetOverrideShadowOn(shadowOn) {
    this.fLayout.SetOverrideShadowOn(shadowOn);
  }

  SetShadowOn(shadowOn) {
    this.fLayout.SetShadowOn(shadowOn);
  }

  GetShadowOn() {
    return this.fLayout.GetShadowOn();
  }

  SetBranchStyle(branchStyle, doOverride) {
    var myLayout = this.fLayout;
    // kb 5/11/2022.
    // change here is intended to set branch style of container when manually assigning branch style to container member in wb
    // level containing all containers would not respect manual style being set
    // during optimization, a manually assigned branch style belonging to a container's layout will now persist
    if (myLayout.fContainerObjID != null) {
      var pageLayoutMgr = this.nodeRef.fPageMgr;
      var containerNode = pageLayoutMgr.FindNodeFromDataObjID(myLayout.fContainerObjID);
      if (containerNode != null) {
        containerNode.GetLayout().SetBranchStyle(branchStyle, doOverride);
      }
    }
    myLayout.SetBranchStyle(branchStyle, doOverride);
  }

  GetBranchStyle() {
    return this.fLayout.GetBranchStyle();
  }

  //====================================================================
  // Annotations
  GetAnnoFlag() {
    return this.fLayout.GetFlagType();
  }

  GetAnnoColor1() {
    return this.fLayout.GetFlagColor1();
  }

  GetAnnoColor2() {
    return this.fLayout.GetFlagColor2();
  }

  GetAnnoColor3() {
    return this.fLayout.GetFlagColor3();
  }

  GetTriPosition() {
    return this.fLayout.GetTriPosition();
  }

  GetAnnoLabel() {
    return this.fLayout.GetFlagLabel();
  }

  //====================================================================
  // Box Margins and Spacing
  SetBoxMargins(margins) {
    this.fLayout.SetBoxMargins(margins);
  }

  GetBoxMargins() {
    return this.fLayout.GetBoxMargins();
  }

  SetBoxSpacing(spacing) {
    this.fLayout.SetBoxSpacing(spacing);
  }

  GetBoxSpacing() {
    return this.fLayout.GetBoxSpacing();
  }

  SetOverrideBoxSpacing(spacing) {
    this.fLayout.SetOverrideBoxSpacing(spacing);
  }

  // MgrVSpacing1
  SetOverrideMgrVSpacing1(spacing) {
    this.fLayout.SetOverrideMgrVSpacing1(spacing);
  }

  SetMgrVSpacing1(vSpacing) {
    this.fLayout.SetMgrVSpacing1(vSpacing);
  }

  // MgrVSpacing2
  SetOverrideMgrVSpacing2(spacing) {
    this.fLayout.SetOverrideMgrVSpacing2(spacing);
  }

  SetMgrVSpacing2(vSpacing) {
    this.fLayout.SetMgrVSpacing2(vSpacing);
  }

  GetContentLock() {
    var dbObj = this.GetDataObj();
    if (dbObj == null) return false;

    var lockStatus = dbObj.GetLockStatus(null);
    var contentLocked = ContentLocked(lockStatus);
    return contentLocked;
  }

  SetContentLock(lockContent) {
    var dbObj = this.GetDataObj();
    if (dbObj == null) return false;

    var curPosLock = this.GetPositionLock();
    var lockFlag = ConvertToLockFlag(lockContent, curPosLock);
    dbObj.SetLockStatus(lockFlag);
  }

  GetPositionLock() {
    var dbObj = this.GetDataObj();
    if (dbObj == null) return false;

    var lockStatus = dbObj.GetLockStatus(null);
    var contentLocked = PositionLocked(lockStatus);
    return contentLocked;
  }

  SetPositionLock(lockPosition) {
    var dbObj = this.GetDataObj();
    if (dbObj == null) return false;

    var curContentLock = this.GetContentLock();
    var lockFlag = ConvertToLockFlag(curContentLock, lockPosition);
    dbObj.SetLockStatus(lockFlag);
    return true;
  }

  GetPersistLock() {
    var persistIt = false;
    var dbObj = this.GetDataObj();
    if (dbObj == null) return persistIt;

    var persistStatus = dbObj.GetPersistStatus(null);
    persistIt = persistStatus == enumPersistStatus.persist;

    return persistStatus;
  }

  SetPersistLock(persistLock) {
    var dbObj = this.GetDataObj();
    if (dbObj == null) return false;

    var persistStatus = enumPersistStatus.nopersist;
    if (persistLock) persistStatus = enumPersistStatus.persist;

    dbObj.SetPersistStatus(persistStatus);
    return true;
  }

  GetLayoutLock() {
    var l = this.GetLayout();
    if (!l) console.log("warning!");
    var scNode = l.GetSCNodeRef();
    return scNode.IsLayoutLocked();
  }

  SetLayoutLock(lockLayout) {
    var l = this.GetLayout();
    var scNode = l.GetSCNodeRef();
    scNode.LockLayout(lockLayout);
  }

  GetMgrVSpacing1() {
    return this.fLayout.GetMgrVSpacing1();
  }

  GetMgrVSpacing2() {
    return this.fLayout.GetMgrVSpacing2();
  }

  GetMarginMode() {
    return this.fLayout.GetMarginMode();
  }

  SetMarginMode(marginMode) {
    this.fLayout.SetMarginMode(marginMode);
  }

  SetOverrideMarginMode(marginMode) {
    this.fLayout.SetOverrideMarginMode(marginMode);
  }

  ShouldDoOverrides() {
    //TODO: this is likely an inadequate check, but will suffice for now.
    return this.GetDataObj() != null;
  }

  //====================================================================
  GetContainerNode() {
    var nodeRef = this["nodeRef"];
    if (nodeRef) return nodeRef.GetContainerNode();
  }

  GetContainerType() {
    var nodeRef = this["nodeRef"];
    return nodeRef.GetContainerType();
  }

  GetContainerNumCols() {
    var nodeRef = this["nodeRef"];
    return nodeRef.GetContainerNumCols();
  }

  SetContainerNumCols(numCols) {
    var nodeRef = this["nodeRef"];
    return nodeRef.SetContainerNumCols(numCols);
  }

  SetContainerTopLinesToColumns(doSet) {
    var l = this.GetLayout();
    l.SetContainerTopLinesToColumns(doSet);
  }

  SetContainerBottomLinesToColumns(doSet) {
    var l = this.GetLayout();
    l.SetContainerBottomLinesToColumns(doSet);
  }

  GetContainerTopLinesToColumns() {
    var l = this.GetLayout();
    return l.GetContainerTopLinesToColumns();
  }

  GetContainerBottomLinesToColumns() {
    var l = this.GetLayout();
    return l.GetContainerBottomLinesToColumns();
  }

  SetContainerType(containerType) {
    var l = this.GetLayout();
    l.SetContainerType(containerType);
  }

  GetAssociatedNodes() {
    var nodeRef = this["nodeRef"];
    return nodeRef.GetAssociatedNodes();
  }

  //====================================================================
  // Shadow — merged into DoRenderElement for fewer save/restore pairs
  RenderShadowAndBorder(drawBuffer) {
    // Shadow is now handled inside DoRenderElement.
    // DoRenderBorder is already a no-op for QChartBox.
  }

  DoRenderShadow(drawBuffer) {
    // Retained for subclass compatibility, but no longer called from RenderShadowAndBorder.
    // Shadow rendering is inlined in DoRenderElement.
  }

  CreateBorderPath(drawBuffer, padding = 0) {
    var borderWidth = this.GetAdjBorderWidth(enumElementBorderStyle.noBorder);
    var panelX = this.GetPositionX();
    var panelY = this.GetPositionY();

    var sx = panelX - borderWidth - padding;
    var sy = panelY - borderWidth - padding;
    var ex = panelX + this.GetWidth() + borderWidth + padding;
    var ey = panelY + this.GetHeight() + borderWidth + padding;
    var cornerRadius = 8;

    this.DoCreateBorderPath(drawBuffer, sx, sy, ex, ey, cornerRadius, true);
  }

  DoCreateBorderPath(drawBuffer, sx, sy, ex, ey, radius, closePath) {
    var ctx = drawBuffer.getContext("2d");
    var r = radius;
    var r2d = Math.PI / 180;

    ctx.beginPath();
    var boxShape = this.GetBoxShapeType();
    switch (boxShape) {
      case enumBoxShapes.rectangle:
        ctx.moveTo(sx, sy);
        ctx.lineTo(ex, sy);
        ctx.lineTo(ex, ey);
        ctx.lineTo(sx, ey);
        ctx.lineTo(sx, sy);
        break;
      case enumBoxShapes.roundRectangle:
        ctx.moveTo(sx + r, sy);
        ctx.lineTo(ex - r, sy);
        ctx.arc(ex - r, sy + r, r, r2d * 270, r2d * 360, false);
        ctx.lineTo(ex, ey - r);
        ctx.arc(ex - r, ey - r, r, r2d * 0, r2d * 90, false);
        ctx.lineTo(sx + r, ey);
        ctx.arc(sx + r, ey - r, r, r2d * 90, r2d * 180, false);
        ctx.lineTo(sx, sy + r);
        ctx.arc(sx + r, sy + r, r, r2d * 180, r2d * 270, false);
        break;
      case enumBoxShapes.snipCornerRectangle:
        ctx.moveTo(sx, sy);
        ctx.lineTo(ex - r, sy);
        ctx.lineTo(ex, sy + r);
        ctx.lineTo(ex, ey);
        ctx.lineTo(sx, ey);
        ctx.lineTo(sx, sy);
        break;
      case enumBoxShapes.roundSnipCornerRectangle:
        ctx.moveTo(sx, sy);
        ctx.lineTo(ex - r, sy);
        ctx.arc(ex - r, sy + r, r, r2d * 270, r2d * 360, false);
        ctx.lineTo(ex, ey);
        ctx.lineTo(sx, ey);
        ctx.lineTo(sx, sy);
        break;
    }

    if (closePath) ctx.closePath();
  }

  DrawAnnoLabel(drawBuffer, theLabel, sx, sy, tSize, triColor, triPosition) {
    var ctx = drawBuffer.getContext("2d");
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";

    var fillStyle = "#000000";
    if (triColor != null)
      fillStyle = DetermineBrightness(triColor) < gBrightnessThreshold ? "#ffffff" : "#000000";

    ctx.fillStyle = fillStyle;
    SetCtxFont(ctx, "Bold 11px Tahoma");

    var t3 = tSize * 0.3333;
    var r = triPosition == 0 ? sx + tSize - t3 : sx - tSize + t3;

    //	ctx.fillText(theLabel,sx+tSize*.75-1,sy+tSize*.25);
    ctx.fillText(theLabel, r, sy + t3);
    //	ctx.fillText(theLabel,sx+t*3,sy+t);
  }

  DrawTri(ctx, isx, isy, iex, iey, radius, triColor, boxShape) {
    var r = radius;
    var r2d = Math.PI / 180;
    ctx.beginPath();

    if (isx > iex) {
      switch (boxShape) {
        case enumBoxShapes.rectangle:
        case enumBoxShapes.roundSnipCornerRectangle:
        case enumBoxShapes.snipCornerRectangle:
          ctx.moveTo(iex, isy);
          ctx.lineTo(iex, iey);
          ctx.lineTo(isx, isy);
          ctx.lineTo(iex, isy);
          break;
        case enumBoxShapes.roundRectangle:
          ctx.moveTo(isx, isy);
          ctx.lineTo(iex + r, isy);
          ctx.arc(iex + r, isy + radius, radius, r2d * 270, r2d * 180, true);
          ctx.lineTo(iex, iey);
          ctx.lineTo(isx, isy);
          break;
      }
    } else {
      switch (boxShape) {
        case enumBoxShapes.rectangle:
          ctx.moveTo(iex, isy);
          ctx.lineTo(iex, iey);
          ctx.lineTo(isx, isy);
          ctx.lineTo(iex, isy);
          break;
        case enumBoxShapes.roundRectangle:
        case enumBoxShapes.roundSnipCornerRectangle:
          ctx.moveTo(isx, isy);
          ctx.lineTo(iex - r, isy);
          ctx.arc(iex - r, isy + radius, radius, r2d * 270, r2d * 360, false);
          ctx.lineTo(iex, iey);
          ctx.lineTo(isx, isy);
          break;
        case enumBoxShapes.snipCornerRectangle:
          ctx.moveTo(isx, isy);
          ctx.lineTo(iex - r, isy);
          ctx.lineTo(iex, isy + radius);
          ctx.lineTo(iex, iey);
          ctx.lineTo(isx, isy);
          break;
      }
    }
    ctx.closePath();
    ctx.fillStyle = triColor;
    ctx.fill();
  }

  GetAnnoRect() {
    var sx = this.GetPositionX();
    var sy = this.GetPositionY();
    var ex = sx + this.GetWidth();
    var r = new QRect();
    r.InitRect(ex - kTriAnnoW, sy, ex, sy + kTriAnnoW);
    return r;
  }

  DrawAnno(drawBuffer, startx, endx, sy, radius, lineThickness) {
    var drawAnno = this.GetAnnoFlag();
    if (drawAnno == enumFlagType.none) return;

    var triPosition = this.GetTriPosition();
    var triColor = this.GetAnnoColor1();

    var ctx = drawBuffer.getContext("2d");
    ctx.save();

    var tSize = kTriAnnoW; //20

    var adj = (lineThickness - 1) / 2;

    var ex, sy, sx, ey, isx, isy, iex, iey, lx;
    sy = sy + adj;
    ey = sy + tSize;
    isy = sy + 0.5;
    iey = ey - 0.5;

    if (triPosition == 0) {
      ex = endx - adj;
      sx = ex - tSize;
      isx = sx + 0.5;
      iex = ex - 0.5;
      lx = sx;
    } else {
      ex = startx + adj;
      sx = ex + tSize;
      isx = sx - 0.5;
      iex = ex + 0.5;
      lx = sx;
    }

    if (triColor != null) {
      var boxShape = this.GetBoxShapeType();
      this.DrawTri(ctx, isx, isy, iex, iey, radius, triColor, boxShape);
      var subTriColor = this.GetAnnoColor3();
      if (subTriColor != null) {
        var isx2;
        if (triPosition == 0) isx2 = isx + kMiniTriWidth;
        else isx2 = isx - kMiniTriWidth;

        var isy2 = isy + kMiniTriWidth; //8
        this.DrawTri(ctx, isx2, isy2, iex, iey, radius, subTriColor, enumBoxShapes.rectangle);
      }
    }

    var dashColor = this.GetAnnoColor2();
    if (dashColor == null) {
      ctx.strokeStyle = this.GetBoxBorderColor();
      ctx.lineWidth = 1;
    } else {
      ctx.strokeStyle = dashColor;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 2]);
    }

    ctx.beginPath();
    ctx.moveTo(isx, isy);
    ctx.lineTo(iex, iey);
    //ctx.closePath();
    ctx.lineJoin = "bevel";
    ctx.stroke();

    var annoLabel = this.GetAnnoLabel();
    if (annoLabel != null)
      this.DrawAnnoLabel(drawBuffer, annoLabel, lx, sy, tSize, triColor, triPosition);

    ctx.restore();
  }

  SpecialRenderBorder(
    drawBuffer,
    sx,
    sy,
    ex,
    ey,
    lineThickness,
    radius,
    fillShape,
    drawBorder,
    drawShadow,
    dashStyle
  ) {
    if (drawShadow == false && fillShape == false && drawBorder == false) return;

    var ctx = drawBuffer.getContext("2d");
    ctx.save();

    this.DoCreateBorderPath(drawBuffer, sx, sy, ex, ey, radius, true);

    if (drawShadow) {
      ctx.shadowOffsetX = kBoxShadowOffset;
      ctx.shadowOffsetY = kBoxShadowOffset;
      ctx.shadowBlur = 5;
      ctx.shadowColor = "rgba(120, 120, 120, 0.8)";
      ctx.fill();
    }

    if (fillShape) {
      var fillMode = this.GetBoxFillMode();
      if (fillMode == enumFillMode.solidFill) {
        ctx.fillStyle = this.GetBoxFillColor();
        ctx.fill();
      } else if (fillMode == enumFillMode.linearGradientDown) {
        var fillStyle = this.GetBoxFillColor();
        var fillStyle2 = this.GetBoxFillColorGradient();

        var grd = ctx.createLinearGradient(sx, sy, sx, ey);
        if (fillStyle == null) fillStyle = "#FFFFFF";
        if (fillStyle2 == null) fillStyle2 = "#FFFFFF";
        grd.addColorStop(0, fillStyle);
        grd.addColorStop(1, fillStyle2);
        ctx.fillStyle = grd;
        ctx.fill();
      }
    }

    if (drawBorder) {
      ctx.strokeStyle = this.GetBoxBorderColor();
      ctx.lineWidth = lineThickness;
      ctx.lineJoin = "miter";

      if (dashStyle != enumDashStyle.nodash) {
        this.DoCreateBorderPath(drawBuffer, sx, sy, ex, ey, radius, false);
        ctx.setLineDash([5, 4]);
      }
      ctx.stroke();
    }

    ctx.restore();
  }

  DoRenderBorder(drawBuffer) {
    // Border rendering done differently than base QElement
  }

  CanAddSub() {
    if (this.GetDoc().IsPositionDocument()) {
      if (!this.IsPositionBox()) return false;
    }
    if (this.TopOfSubChartBreak()) return false;

    return true;
  }

  HoverTarget(becomeTarget) {
    if (this.fWSMgr == null) return;

    var hp = this.fWSMgr.GetHoverProfile();
    if (hp == null) return;

    var isSticky = hp.GetSticky();
    if (isSticky) return;

    var t = null;
    if (becomeTarget) t = this;
    hp.SetHoverTarget(t);
  }

  HandleHyperlink(theCell) {
    if (theCell == null) return;

    var layoutBox = this.GetBoxMode() === enumChartBoxInteractionMode.layout;
    if (!theCell.IsHyperLink() || layoutBox) return;

    var theData = theCell.GetLinkContent(true);
    if (theData) {
      var theLink = GetLaunchHyperlink(theData);
      window.open(theLink);
    }
  }

  HasActiveSubs() {
    return this.fLayoutCells.some(function (aLayoutCell) {
      return aLayoutCell.GetLinkType() !== false;
    });
  }

  SetSubElement(subElement) {
    this.fSubElement = subElement;
  }

  DoSetSelected(priorState, selected) {
    var w = this.fWSMgr;
    if (w == null) return;

    this.HandleHyperlink(this.fSubElement);
    this.SetSubElement(null);

    var hp = this.fWSMgr.GetHoverProfile();
    if (hp == null) return;

    //	var isSticky = hp.GetSticky();
    hp.SetSticky(false);
    if (selected) {
      this.HoverTarget(true);
      hp.SetSticky(true);
    } else if (this == hp.GetTargetBox()) this.HoverTarget(false);
  }

  IsHoverHighlightEnabled() {
    var p = this.fParentPanel;
    if (!p) console.log(":(");
    var hoverEnabled = p.IsHoverHighlightEnabled();
    if (hoverEnabled) {
      if (p.IsElementChartBox(this, false) && this.CanAddSub()) return true;
    }

    return false;
  }

  GetDropType() {
    return this.fParentPanel.GetDropType();
  }

  RenderHoverHighlight(drawBuffer, sx, sy, width, height) {
    let isHighlighted = this.GetDrawState() == enumElementDrawStates.Hover || this.fIsHighlighted;
    //this.IsHoverHighlightEnabled() || forceHoverRender
    if (isHighlighted && !this.fParentPanel?.fNoHighlight) {
      this.RenderSelectionHighlight(drawBuffer, "#FFFFFF", true);
    }
  }

  SetHighlightInfo(highlightName, highlightValue, highlightType) {
    var layout = this.GetLayout();
    layout.SetHighlightInfo(highlightName, highlightValue, highlightType);
  }

  GetHighlightInfo() {
    var layout = this.GetLayout();
    if (!layout) console.log("???");
    return layout.GetHighlightInfo();
  }

  DropTypeTest(x) {
    var dropMargin = 30;
    var indicator = enumDropType.subordinate;
    var elementX = x - this.GetPositionX();
    //NODE- enumdroptype.mrbadd is double used by both a contained box and the container box. May be good to seperate later?
    if (this.GetContainerType() != enumContainerType.nonmrb || this.GetContainerNode() != null)
      return enumDropType.mrbadd;

    if (elementX < dropMargin) return enumDropType.leftcoworker;

    var sx = this.GetWidth() - dropMargin;
    if (elementX > sx) return enumDropType.rightcoworker;

    return indicator;
  }

  GetBoundingRect() {
    var l = this.GetLayout();
    var r = l.GetBoundingRect();
    return r;
  }

  DoRenderDropIndicators(drawBuffer) {
    var dI = this.GetDropType();

    // Allow the highlight for valid moves for Plan Positions, but not much more.
    const disallowedPlanningRenderMode =
      Globals._uiConfig.GetUIMode() == enumUIModes.planning && dI != enumDropType.mrbadd;
    if (dI == enumDropType.none || disallowedPlanningRenderMode) return;

    if (!this.fParentPanel.ValidMove(dI)) {
      return;
    }
    var plm = this.fWSMgr.fCanvasPanel.GetPageLayoutMgr();
    var chartType = plm.GetChartType();
    var ctx = drawBuffer.getContext("2d");

    var l = this.GetLayout();
    var r = l.GetBoundingRect();
    var sx = r.fLeft;
    var sy = r.fTop;
    var ex = r.fRight;
    var ey = r.fBottom;

    ctx.save();
    ctx.strokeStyle = "#000000";

    var t0x, t0y, t1x, t1y, t2x, t2y;
    var doDrawTri = true;
    switch (dI) {
      case enumDropType.subordinate:
        t0x = sx + Math.floor((ex - sx) / 2);
        t1x = t0x + 5;
        t2x = t0x - 5;
        if (chartType == enumChartTypes.inverted) {
          //above the box
          t0y = sy - 8;
          t1y = t0y + 5;
          t2y = t0y + 5;
        } else {
          // Below the box
          t0y = ey + 8;
          t1y = t0y - 5;
          t2y = t0y - 5;
        }
        break;
      case enumDropType.rightcoworker:
        t0x = ex + 8;
        t0y = sy + Math.floor((ey - sy) / 2);
        t1x = t0x - 5;
        t1y = t0y - 5;
        t2x = t0x - 5;
        t2y = t0y + 5;
        break;
      case enumDropType.leftcoworker:
        t0x = sx - 8;
        t0y = sy + Math.floor((ey - sy) / 2);
        t1x = t0x + 5;
        t1y = t0y - 5;
        t2x = t0x + 5;
        t2y = t0y + 5;
        break;
      case enumDropType.mrbadd:
        doDrawTri = false;
        var theContainer = this;
        if (this.GetContainerType() == enumContainerType.nonmrb) {
          theContainer = this.GetContainerNode().fChartBox;
        }
        var l = theContainer.GetLayout();
        var r = l.GetBoundingRect(false);
        var sx = r.fLeft;
        var sy = r.fTop;
        var ex = r.fRight;
        var ey = r.fBottom;

        ctx.strokeStyle = "#00FF00";
        ctx.lineWidth = 2;
        ctx.lineJoin = "miter";
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(ex, sy);
        ctx.lineTo(ex, ey);
        ctx.lineTo(sx, ey);
        ctx.lineTo(sx, sy);
        ctx.closePath();
        ctx.stroke();
        //ctx.fill();
        ctx.restore();
        break;
    }
    if (doDrawTri) {
      ctx.fillStyle = "#00FF00";
      ctx.lineWidth = 2;
      ctx.lineJoin = "miter";

      ctx.beginPath();
      ctx.moveTo(t0x, t0y);
      ctx.lineTo(t1x, t1y);
      ctx.lineTo(t2x, t2y);
      ctx.lineTo(t0x, t0y);
      ctx.closePath();

      ctx.stroke();
      ctx.fill();

      ctx.restore();
    }
  }

  RenderSelectionHighlight(drawBuffer, fillColor, renderAsHighlight = false) {
    if (this.IsStaticPicture() && this.GetLayer() !== enumElementLayer.master) return;
    if (!renderAsHighlight && this.fParentPanel?.IsKeyboardFocusOnly?.(this)) return;

    var ctx = drawBuffer.getContext("2d");
    ctx.save();
    this.SetChartBoxClip(drawBuffer, renderAsHighlight ? 4 : 0);

    ctx.fillStyle = "#6c9ed4";
    ctx.strokeStyle = "#7EBFFF";
    ctx.linewidth = 10;

    ctx.stroke();
    ctx.globalAlpha = renderAsHighlight ? 0.2 : 0.4;
    ctx.fill();
    ctx.restore();

    if (!renderAsHighlight) {
      this.DoRenderDropIndicators(drawBuffer);
    }
  }

  // TORK - Chart boxes can be on both the master and in the content
  DoAltRender(altCanvas) {
    var r = this.GetAdjBounds(kRoundRectRadius);
    var r1 = this.GetOuterBorder(r, kRoundRectRadius);
    var boxShape = this.GetBoxShapeType();
    var fillMode = this.GetBoxFillMode();
    var fillColor = this.GetBoxFillColor();
    var backColor = this.GetBoxFillColorGradient();
    var borderStyle = this.GetBorderStyle();
    var borderThickness = this.GetAdjBorderThickness();
    var borderColor = this.GetBoxBorderColor();
    var shadow = this.GetShadowOn();
    var dashStyle = this.GetDashStyle();

    var altCanvasType = altCanvas.GetCanvasType();
    var altCanvasElement = altCanvas.GetCanvasElement();

    switch (altCanvasType) {
      case "SVG":
        var r2 = r.Clone();
        SVG_RenderChartBox(
          r2,
          r1,
          boxShape,
          fillMode,
          fillColor,
          borderStyle,
          borderThickness,
          borderColor,
          dashStyle,
          shadow,
          altCanvasElement,
          backColor
        );
        break;
      case "XML":
        var layer = this.GetLayer();
        var r2 = r.Clone();
        XML_RenderChartBox(
          r2,
          r1,
          boxShape,
          fillMode,
          fillColor,
          borderStyle,
          borderThickness,
          borderColor,
          dashStyle,
          shadow,
          altCanvasElement,
          layer,
          backColor
        );
        break;
    }
  }

  DoPostAltRender(altCanvas) {
    var r = this.GetAdjBounds(kRoundRectRadius);
    var boxShape = this.GetBoxShapeType();

    var borderThickness = this.GetAdjBorderThickness();
    var borderColor = this.GetBoxBorderColor();

    var altCanvasType = altCanvas.GetCanvasType();
    var altCanvasElement = altCanvas.GetCanvasElement();

    var annoFlag = this.GetAnnoFlag();
    var drawAnno = annoFlag != enumFlagType.none;
    if (!drawAnno) return;

    var triColor = this.GetAnnoColor1();
    var dashColor = this.GetAnnoColor2();
    var subTriColor = this.GetAnnoColor3();
    var triPosition = this.GetTriPosition();

    var annoLabel = this.GetAnnoLabel();

    switch (altCanvasType) {
      case "SVG":
        SVG_RenderAnno(
          r,
          boxShape,
          triColor,
          dashColor,
          subTriColor,
          annoLabel,
          triPosition,
          borderColor,
          borderThickness,
          altCanvasElement
        );
        break;
      case "XML":
        var layer = this.GetLayer();
        XML_RenderAnno(
          r,
          boxShape,
          triColor,
          dashColor,
          subTriColor,
          annoLabel,
          triPosition,
          borderColor,
          borderThickness,
          altCanvasElement,
          layer
        );
        break;
    }
  }

  GetLinkBookmark() {
    var flag = this.GetSubChartFlag();
    if (flag == enumSubChartFlag.nobreak) return null;

    var subChartID = this.DrillSubChartID();
    if (subChartID == -1) return null;

    return this.fParentPanel.GetLinkBookmark(subChartID);
  }

  GetAdjBorderThickness() {
    var borderStyle = this.GetBorderStyle();
    var lineWidth = this.GetBorderThickness();
    if (borderStyle == enumElementBorderStyle.noBorder) lineWidth = 0;

    return lineWidth;
  }

  GetBorderOutset() {
    var lineWidth = this.GetAdjBorderThickness();
    var borderOutset = Div2Floor(lineWidth) + ThicknessAdjuster(lineWidth);
    return borderOutset;
  }

  GetAdjBounds(cornerRadius) {
    var borderWidth = this.GetBorderOutset();

    var panelX = this.GetPositionX();
    var panelY = this.GetPositionY();

    var sx = panelX - borderWidth;
    var sy = panelY - borderWidth;
    var ex = panelX + this.GetWidth() + borderWidth;
    var ey = panelY + this.GetHeight() + borderWidth;

    var r = new QRoundRect();
    r.InitRoundRect(sx, sy, ex, ey, cornerRadius);

    return r;
  }

  GetAdjBounds2(cornerRadius, borderStyle) {
    var borderWidth = this.GetAdjBorderThickness();
    var thickAdj = ThicknessAdjuster(1);

    var panelX = this.GetPositionX();
    var panelY = this.GetPositionY();

    var doubleB = 0;
    if (borderStyle == enumElementBorderStyle.doubleBorder) doubleB = kDoubleBorderWidthAdder + 1;

    var sx = panelX - borderWidth - doubleB - thickAdj;
    var sy = panelY - borderWidth - doubleB - thickAdj;
    var ex = panelX + this.GetWidth() + borderWidth + doubleB + thickAdj;
    var ey = panelY + this.GetHeight() + borderWidth + doubleB + thickAdj;

    var r = new QRoundRect();
    r.InitRoundRect(sx, sy, ex, ey, cornerRadius);

    return r;
  }

  GetOuterBorder(border, cornerRadius) {
    var r1 = new QRoundRect();

    var borderOutset = this.GetBorderOutset();
    var thickAdj = ThicknessAdjuster(1);

    var sx = border.fLeft - borderOutset - kDoubleBorderWidthAdder - thickAdj;
    var sy = border.fTop - borderOutset - kDoubleBorderWidthAdder - thickAdj;
    var ex = border.fRight + borderOutset + kDoubleBorderWidthAdder + thickAdj;
    var ey = border.fBottom + borderOutset + kDoubleBorderWidthAdder + thickAdj;
    var outerRadius = cornerRadius + borderOutset + 2;

    r1.InitRoundRect(sx, sy, ex, ey, outerRadius);
    return r1;
  }

  RenderHoverProfile(drawBuffer, rBounds) {
    if (this.GetDrawState() != enumElementDrawStates.Hover) return;

    var ctx = drawBuffer.getContext("2d");
    ctx.fillStyle = "#FF1010";

    ctx.beginPath();
    ctx.arc(rBounds.fLeft, rBounds.fBottom, 3, 0, 2 * Math.PI, false);
    ctx.fill();
  }

  RenderSpacing(drawBuffer, left, top, width, height) {
    var inverted = this.fParentPanel.GetPageLayoutMgr().GetChartType() === enumChartTypes.inverted;
    var ctx = drawBuffer.getContext("2d");

    ctx.save();
    ctx.strokeStyle = "#CC9999";
    ctx.fillStyle = "#000000";
    ctx.lineWidth = 1;
    ctx.lineJoin = "miter";

    var margins = this.GetBoxMargins();
    var spacing = this.GetBoxSpacing();
    var right = left + width;
    var bottom = top + height;
    var midX = Div2Floor(width) + 1;
    var midY = Div2Floor(height) + 1;

    var sleft = left - spacing.fLeft;
    var sright = right + spacing.fRight;
    var stop = top - spacing.fTop;
    var sbot = bottom + spacing.fBottom;
    var smidx = left + midX;
    var smidy = top + midY;

    if (spacing.fLeft != 0) DrawBSpacer(ctx, sleft, smidy, left, smidy, 3);
    if (spacing.fRight != 0) DrawBSpacer(ctx, sright, smidy, right, smidy, 3);
    if (spacing.fTop != 0) DrawBSpacer(ctx, smidx, stop, smidx, top, 3);
    if (spacing.fBottom != 0) DrawBSpacer(ctx, smidx, sbot, smidx, bottom, 3);

    var mB = inverted ? margins.fTop : margins.fBottom;
    var mT = inverted ? margins.fBottom : margins.fTop;
    var scH = GetSCArrowHeight();

    var topBreak = inverted ? this.BottomOfSubChartBreak(false) : this.TopOfSubChartBreak();
    if (topBreak) mB = Math.max(mB, scH + 4);

    var bottomBreak = inverted ? this.TopOfSubChartBreak() : this.BottomOfSubChartBreak(false);
    if (bottomBreak) mT = Math.max(mT, scH + 4);

    var sx = left - margins.fLeft + 1;
    var sy = top - mT + 1;
    var ex = right + margins.fRight - 1;
    var ey = bottom + mB - 1;

    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(ex, sy);
    ctx.lineTo(ex, ey);
    ctx.lineTo(sx, ey);
    ctx.lineTo(sx, sy);
    ctx.closePath();
    ctx.setLineDash([5, 2]);
    ctx.stroke();
    ctx.globalAlpha = 0.05;
    ctx.fill();

    var showMgrSpacing = !topBreak && this.GetNumChildren() > 0;
    if (showMgrSpacing) {
      var nRef = this["nodeRef"];
      var bAdder;
      if (margins.fBottom > spacing.fBottom) bAdder = 0;
      else bAdder = spacing.fBottom - margins.fBottom;

      bAdder += nRef.GetMgrGap();
      var mgrVS2 = this.GetMgrVSpacing2() + bAdder + 1;

      if (mgrVS2 > 0) DrawBSpacer(ctx, smidx + 10, ey + mgrVS2, smidx + 10, ey, 2);
      var mgrVS1 = this.GetMgrVSpacing1();
      if (mgrVS1 > 0) DrawBSpacer(ctx, smidx + 15, ey + mgrVS1 + mgrVS2, smidx + 15, ey, 2);
    }

    ctx.restore();
  }

  // Rendering of the chart box — shadow is now inlined here (was DoRenderShadow)
  DoRenderElement(drawBuffer) {
    var r = this.GetAdjBounds(kRoundRectRadius);
    var lineWidth = this.GetAdjBorderThickness();
    var borderStyle = this.GetBorderStyle();
    var dashStyle = this.GetDashStyle();
    var parentPanel = this.fParentPanel;

    // --- Shadow pass (merged from DoRenderShadow) ---
    if (this.GetShadowOn()) {
      var ctx = drawBuffer.getContext("2d");
      var borderSize = this.GetBorderWidth(borderStyle);
      var borderThickness = this.GetBorderThickness(borderStyle);
      if (borderStyle == enumElementBorderStyle.noBorder) borderThickness = 0;

      var panelX = this.GetPositionX();
      var panelY = this.GetPositionY();

      this.DoCreateBorderPath(
        drawBuffer,
        panelX - borderSize,
        panelY - borderSize,
        panelX + this.GetWidth() + borderSize,
        panelY + this.GetHeight() + borderSize,
        8,
        true
      );
      ctx.shadowOffsetX = kBoxShadowOffset;
      ctx.shadowOffsetY = kBoxShadowOffset;
      ctx.shadowBlur = 5;
      ctx.shadowColor = "rgba(120, 120, 120, 0.8)";
      ctx.fill();
      // Clear shadow state instead of save/restore
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.shadowBlur = 0;
      ctx.shadowColor = "transparent";
    }

    // --- Fill + Border pass ---
    switch (borderStyle) {
      case enumElementBorderStyle.noBorder:
        this.SpecialRenderBorder(
          drawBuffer,
          r.fLeft,
          r.fTop,
          r.fRight,
          r.fBottom,
          lineWidth,
          kRoundRectRadius,
          true,
          false,
          false,
          dashStyle
        );
        break;
      case enumElementBorderStyle.singleBorder:
        this.SpecialRenderBorder(
          drawBuffer,
          r.fLeft,
          r.fTop,
          r.fRight,
          r.fBottom,
          lineWidth,
          kRoundRectRadius,
          true,
          true,
          false,
          dashStyle
        );
        break;
      case enumElementBorderStyle.doubleBorder:
        var r1 = this.GetOuterBorder(r, kRoundRectRadius);
        //var outerRadius = cornerRadius + borderOutset + 2;
        this.SpecialRenderBorder(
          drawBuffer,
          r1.fLeft,
          r1.fTop,
          r1.fRight,
          r1.fBottom,
          1,
          r.fCornerRadius,
          true,
          true,
          false,
          dashStyle
        );
        this.SpecialRenderBorder(
          drawBuffer,
          r.fLeft,
          r.fTop,
          r.fRight,
          r.fBottom,
          lineWidth,
          kRoundRectRadius,
          false,
          true,
          false,
          enumDashStyle.nodash
        );
        break;
    }

    //Moved to post render.
    //this.DrawAnno(drawBuffer,r.fLeft, r.fRight,r.fTop, kRoundRectRadius,lineWidth);

    this.RenderHoverHighlight(drawBuffer, r.fLeft, r.fTop, r.fRight - r.fLeft, r.fBottom - r.fTop);

    if (parentPanel.fRenderBoxSpacing) {
      var r2 = this.GetAdjBounds2(kRoundRectRadius, borderStyle);
      this.RenderSpacing(drawBuffer, r2.fLeft, r2.fTop, r2.fRight - r2.fLeft, r2.fBottom - r2.fTop);
    }

    var r2 = this.GetAdjBounds2(kRoundRectRadius, borderStyle); // TODO: I dont think this line is necessary.
  }

  DoPostRender(drawBuffer) {
    var r = this.GetAdjBounds(kRoundRectRadius);
    var lineWidth = this.GetAdjBorderThickness();
    this.DrawAnno(drawBuffer, r.fLeft, r.fRight, r.fTop, kRoundRectRadius, lineWidth);
  }

  SetChartBoxClip(drawBuffer, padding = 0) {
    var ctx = drawBuffer.getContext("2d");
    this.CreateBorderPath(drawBuffer, padding);
    ctx.clip();
  }

  StartFieldDrag(layoutCell, canvasX, canvasY) {
    this.fParentPanel.StartFieldDrag(layoutCell, canvasX, canvasY);
  }

  IsSelectable() {
    // Use the parent panel to globally enable or disable selection (overrides QElement default behavior) and then check if chart box can be selected.
    return this.fParentPanel.BoxSelectEnabled() && this.fIsSelectable;
  }

  IsDraggable() {
    var boxLayer = this.GetLayer();
    if (boxLayer === enumElementLayer.master) return false;
    return this.fParentPanel.BoxDragEnabled() && this.fDragable;
  }

  IsControl() {
    return this.fParentPanel.BoxIsControl();
  }

  IsRoot() {
    var dbObj = this.GetDataObj();
    if (dbObj == null) return true;

    return dbObj.IsRoot();
  }

  NeedsExtraBottomSpace() {
    var nodeRef = this["nodeRef"];
    if (nodeRef.fNodeEntity && nodeRef.fNodeEntity.BottomConnectingLineExists()) {
      return true;
    }
    return false;
  }

  GetNumChildren() {
    var dbObj = this.GetDataObj();
    if (dbObj == null) return 0;

    return dbObj.GetNumChildren();
  }

  IsAssistant() {
    var layout = this.GetLayout();
    return layout.IsAssistant();
  }

  GetDataObj() {
    return this.fDataObj;
  }

  GetRootDataObj() {
    //Should be safe to call on non-containers.
    if (this.fDataObj) return this.fDataObj;

    if (!this.GetAssociatedNodes()) return null;
    //Recursive on purpose.
    if (this.GetAssociatedNodes()?.[0]?.fChartBox)
      return this.GetAssociatedNodes()[0].fChartBox.GetRootDataObj();

    return null;
  }

  GetDataObjID() {
    return this.fDataObjID;
  }

  GetCmdCode() {
    var p = this.fParentPanel;
    const boxCmdCode = p.GetBoxCmdCode();
    if (p.IsElementChartBox(this, false) && (this.CanAddSub() || boxCmdCode === "AddHolder"))
      return boxCmdCode;

    return null;
  }

  IsElementBox(anElement) {
    var elementType = anElement.GetElementType();
    var isBox = false;
    switch (elementType) {
      case enumChartElement.chartBox:
      case enumChartElement.textBox:
        isBox = true;
        break;
    }

    return isBox;
  }

  // After box has moved we need to move cells inside the box as well
  AdjustCellPosition() {
    if (this.fLayoutCells == null) return;

    var xPos = this.fLayout.GetPositionX();
    var yPos = this.fLayout.GetPositionY();
    //	if (this.fHorzOffset != xPos || this.fVertOffset != yPos)
    //	{
    this.fHorzOffset = xPos;
    this.fVertOffset = yPos;
    var numCells = this.fLayoutCells.length;
    for (var i = 0; i < numCells; i++) {
      var aCell = this.fLayoutCells[i];
      if (!this.IsElementBox(aCell)) {
        var cellDesc = aCell.GetCellDesc();
        var x = xPos + cellDesc.GetLocationX();
        var y = yPos + cellDesc.GetLocationY();
        aCell.PositionElement(x, y);
      }
    }
    //	}
  }

  // The position within the panel is managed within the layout object
  GetPositionX() {
    var xPos = this.fLayout.GetPositionX();
    if (xPos != this.fHorzOffset) {
      // This means that the position was changed directly in the layout->SetPosition
      console.log("ChartBox.GetPositionX --- this should not happen...");
    }
    return xPos;
  }

  GetPositionY() {
    var yPos = this.fLayout.GetPositionY();
    if (yPos != this.fVertOffset) {
      console.log("ChartBox.GetPositionY --- this should not happen...");
    }

    return yPos;
  }

  PositionElement(x, y) {
    this.fLayout.SetPosition(x, y);
    this.AdjustCellPosition();
  }

  StartInteractWithElement(canvasX, canvasY, panelX, panelY, shiftDown) {
    var parentPanel = this.fParentPanel;
    if (parentPanel) {
      var allowChartMove =
        this.IsRoot() || this.fLayout.GetSubChartFlag() === enumSubChartFlag.drillup;

      var dragMode = parentPanel.GetDragMode();
      var isChartBox = parentPanel.IsElementChartBox(this, false);
      var isTextBox = parentPanel.IsElementTextBox(this);
      if (this.GetBoxSubType() === enumBoxSubTypes.holderAreaBox) return false;

      if (this.GetLayer() === enumElementLayer.master) return;

      if (shiftDown) return false;

      if (dragMode === enumBoxDragMode.dragModeOff) dragMode = enumBoxDragMode.moveBranch;
      if (isChartBox && dragMode === enumBoxDragMode.moveChart && !allowChartMove) {
        dragMode = enumBoxDragMode.moveBranch;
      } else if (isTextBox) {
        dragMode = enumBoxDragMode.moveChart;
        parentPanel.fBoxDragMode = dragMode;
      }
      switch (dragMode) {
        case enumBoxDragMode.moveChart:
          if (isChartBox) {
            return parentPanel.StartChartDrag(this, panelX, panelY);
          } else {
            var elemDragInfo = this.SetElementDragInfo(
              parentPanel,
              parentPanel,
              "ChartObjMoveDragRelease",
              "ChartObjMoveStart",
              true
            );
            elemDragInfo.EnableInstantDrag();
            elemDragInfo.ShowDragSourceElem();
            return true;
          }
        case enumBoxDragMode.moveBranch:
          if (isChartBox) {
            if (!this.IsDragReady()) {
              return false;
            }

            const doc = this.GetDoc();
            let releaseCmd = "BranchMoveDragRelease";
            // "BranchMoveDragRelease" is for WB-only.
            if (isInPlanning()) {
              releaseCmd = "DoSpawnOverridesEditor";
            }
            if (doc?.IsPositionDocument()) {
              releaseCmd = "DoPositionMgmtDragRelease";
            }
            var elemDragInfo = this.SetElementDragInfo(parentPanel, parentPanel, releaseCmd);
            if (Globals._uiConfig.GetUIMode() !== enumUIModes.edit)
              elemDragInfo.EnableInstantDrag();
            elemDragInfo.ShowDragSourceElem();
            return true;
          }
          break;
        case enumBoxDragMode.unlinkBranch:
          if (isChartBox) return parentPanel.StartUnlinkBranch(this, panelX, panelY);
          break;
      }
    }
    return false;
  }

  IsDragReady() {
    var dragReady = true;
    if (Globals._uiConfig.GetUIMode() == enumUIModes.planning) {
      const wsMgr = this.fWSMgr;
      const planMgr = wsMgr.GetPlanMgr();

      if (!planMgr.CanUpdatePlan()) {
        return false;
      }

      var scm = this.fWSMgr.GetSubChartMgr();
      var curSubChart = scm.GetCurrentSubChart();
      var curTopBoxDBObj = curSubChart.GetTopDBObj();
      // Start of block to determine whether to enable drag on hover.
      var dbObj = this.GetDataObj();
      if (curTopBoxDBObj == this.GetDataObj() || dbObj == null)
        // Exit out when it's the subchart topbox or when there's no DB Obj. No special handling atm for co-heads.
        return false;
      var override = planMgr.GetActiveOverrideObj(dbObj);
      var changeType = override != null ? override.SmartGetFieldValue("Type") : null;
      var unmoveableChangeTypes = [
        enumOverrideType.delete,
        enumOverrideType.lock,
        enumOverrideType.moveout
      ];
      if (unmoveableChangeTypes.includes(changeType)) return false;
      // End of block to determine whether to enable drag on hover.
    }
    return dragReady;
  }

  SurveyCells(property, selectedCells) {
    var parameter = null;
    var prevParameter = null;

    var altCompare =
      property == enumUpdateProperty.cellFillColor || property == enumUpdateProperty.cellFontColor;
    var theLayout = this.GetLayout();

    var numCells = theLayout.GetNumCells();
    for (var i = 0; i < numCells; i++) {
      var cellDesc = theLayout.GetIndexCellDesc(i);
      var proceed = true;
      if (selectedCells) {
        var theCell = this.GetCellFromCellDesc(cellDesc);
        proceed = theCell && theCell.IsSelected();
      }

      if (proceed) {
        var isGutter = cellDesc.IsGutterCell();
        if (!isGutter && !theLayout.SkipProperty(cellDesc, property)) {
          parameter = theLayout.GetTextCellProperty(cellDesc, property);
          if (prevParameter != null) {
            if (altCompare) {
              if (!CPCompare(prevParameter, parameter)) return null;
            } else if (prevParameter != parameter) return null;
          }
          prevParameter = parameter;
        }
      }
    }

    return parameter;
  }

  SelectSelf() {
    this.fParentPanel.ClearSelection();
    this.fParentPanel.DoSelectElement(this, true);
  }

  ForceLayoutRecalc(
    saveSelection // , newLayout)
  ) {
    var theLayout = this.GetLayout();
    if (saveSelection) {
      if (!this.IsSelected()) this.SaveCellSelection(theLayout);
    }

    this.ResetBoxLayout(true);
  }

  SmartAlign() {
    var bStyle = this.GetBorderStyle();
    var shadowOn = this.GetShadowOn();
    if (bStyle == enumElementBorderStyle.noBorder && !shadowOn) {
      var curJust = this.SurveyCells(enumUpdateProperty.cellTextAlign, false);
      if (curJust == "left" || curJust == "right") {
        var branchStyle = this.GetBranchStyle();
        var just = null;
        switch (branchStyle) {
          case enumBranchStyle.onecolumncenterright:
          case enumBranchStyle.onecolumnright:
          case enumBranchStyle.assistantright:
            just = "left";
            break;
          case enumBranchStyle.onecolumncenterleft:
          case enumBranchStyle.onecolumnleft:
          case enumBranchStyle.assistantleft:
            just = "right";
            break;
        }

        if (just != null) this.UpdateBoxCells(just, enumUpdateProperty.cellTextAlign);
      }
    }
  }

  UpdateBoxCells(parameter, property, doOverride) {
    var needRecalc = false;

    var numElements = this.fLayoutCells.length;
    for (var i = 0; i < numElements; i++) {
      var theElement = this.fLayoutCells[i];
      if (!this.IsElementBox(theElement)) {
        var recalcNeeded = theElement.UpdateCellProperty(parameter, property, doOverride);
        if (recalcNeeded) needRecalc = true;
      }
    }

    return needRecalc;
  }

  GetFirstEditableCell() {
    var theLayout = this.GetLayout();
    var nC = theLayout.GetNumCols();
    var nR = theLayout.GetNumRows();

    for (var r = 1; r <= nR; r++) {
      for (var c = 1; c <= nC; c++) {
        var cell = this.GetCell(c, r);
        if (cell.IsEditable()) return cell;
      }
    }

    return null;
  }

  GetAccessibleDescription() {
    const kMaxParts = 4;
    const parts = [];
    for (const cell of this.fLayoutCells) {
      if (parts.length >= kMaxParts) break;
      if (cell.IsPicture() || cell.GetCellDesc().IsHidden()) continue;
      const text = cell.GetDisplayContent();
      if (typeof text === "string" && text.trim()) parts.push(text.trim());
    }

    const flag = this.GetSubChartFlag();
    if (this.fSubChartDrill && this.IsDrillActive()) {
      if (flag === enumSubChartFlag.drilldown) parts.push(appstr("Drill down available"));
      else if (flag === enumSubChartFlag.drillup) parts.push(appstr("Drill up available"));
    }
    return parts.join(", ");
  }

  GetFirstCell() {
    var c = this.GetCell(1, 1);
    var cd = c.GetCellDesc();
    if (cd.IsHidden() || cd.GetField() == null) c = this.FindNextCell(c, true);
    return c;
  }

  GetLastCell() {
    var theLayout = this.GetLayout();
    var c = this.GetCell(theLayout.GetNumCols(), theLayout.GetNumRows());
    var cd = c.GetCellDesc();
    if (cd.IsHidden() || cd.GetField() == null) c = this.FindNextCell(c, false);
    return c;
  }

  GetCell(colIndex, rowIndex) {
    var theLayout = this.GetLayout();
    var cellDesc = theLayout.GetCellDesc(colIndex, rowIndex);
    var theCell = this.GetCellFromCellDesc(cellDesc);
    return theCell;
  }

  GetCellFromCellDesc(cellDesc) {
    for (var i = 0; i < this.fLayoutCells.length; i++) {
      var theElement = this.fLayoutCells[i];
      if (!this.IsElementBox(theElement)) {
        var newCellDesc = theElement.GetCellDesc();
        if (newCellDesc == cellDesc) return theElement;
        else if (newCellDesc.fCol === cellDesc.fCol && newCellDesc.fRow === cellDesc.fRow) {
          //console.log('fallback kicked in');
          return theElement;
        }
      } else {
        //	console.log("Chart Box");
      }
    }

    //console.log("Cell from CellDesc failed.");
    return null;
  }

  FindNextCell(currentCell, forward) {
    var theLayout = this.GetLayout();
    var cellDesc = currentCell.GetCellDesc();
    var numRows = theLayout.GetNumRows() + 2; // Include gutters
    var numCols = theLayout.GetNumCols() + 2; // Include gutters
    var rowIndex = cellDesc.GetRowIndex();
    var colIndex = cellDesc.GetColIndex();

    var done = false;
    var inc = -1;
    if (forward == true) inc = 1;

    var nextCellDesc = null;
    while (!done) {
      colIndex = colIndex + inc;
      if (colIndex >= numCols) {
        colIndex = 0;
        rowIndex = rowIndex + 1;
        if (rowIndex >= numRows) return null; // rowIndex = 0;
      }
      if (colIndex < 0) {
        colIndex = numCols - 1;
        rowIndex = rowIndex - 1;
        if (rowIndex < 0) return null; // rowIndex = numRows-1;
      }

      nextCellDesc = theLayout.GetCellDesc(colIndex, rowIndex);
      if (
        !nextCellDesc.IsGutterCell() &&
        !nextCellDesc.IsHidden() &&
        nextCellDesc.GetField() != null
      )
        done = true;
    }

    var nextElement = this.GetCellFromCellDesc(nextCellDesc);
    return nextElement;
  }

  ElementHandleKeyboard(keyInfo) {
    var ctrlKey = keyInfo.ctrlKey;

    // Let the next level up handle this
    if (ctrlKey == true) return false;

    var charCode = keyInfo.charCode;
    if (charCode == 0) return false;

    switch (charCode) {
      case kBackspaceKey:
      case kDeleteKey:
      case kTabKey:
      case kReturnKey:
        return false;
        break;
    }

    var theCell = this.fParentPanel.EditFirstCell(this);
    var handled = false;
    if (theCell) handled = theCell.ElementHandleKeyboard(keyInfo);

    return handled;
  }

  SetDocDirty() {
    this.fParentPanel.SetDocDirty();
  }

  IsResizerShown() {
    return this.IsSelected() && !this.fParentPanel.InDropMode();
  }

  IsExpanderShown() {
    var showExpander = !this.IsResizerShown();
    if (showExpander) {
      showExpander = this.CanAddSub() && this.GetNumChildren() > 0;
    }
    return showExpander;
  }

  IsDrillActive() {
    return this.fParentPanel.GetPanelMode() != enumPanelMode.panMode;
  }

  GetBoxType() {
    return this.fLayout.GetBoxType();
  }

  GetBoxSubType() {
    if (!this.fLayout) {
      return enumBoxSubTypes.chartBox;
      //this.fLayout = this.nodeRef.fLayout;
      //console.log('Error loading box layout');
    }
    return this.fLayout.GetBoxSubType();
  }

  AddSubChartDrill() {
    var scFlag = this.GetSubChartFlag();
    if (scFlag != enumSubChartFlag.nobreak) {
      this.fSubChartDrill = new QSubChartArrow();
      var chartType = this.fParentPanel.GetPageLayoutMgr().GetChartType();
      this.fSubChartDrill.InitSubChartArrow(this, chartType === enumChartTypes.inverted);
      this.fParentPanel.PushElement(this.fSubChartDrill);
    }
  }

  RemoveSubChartDrill() {
    if (this.fSubChartDrill) {
      this.fParentPanel.RemoveElement(this.fSubChartDrill);
      this.fSubChartDrill = null;
    }
  }

  DrillSubChartID() {
    var layout = this.GetLayout();
    var parentSubChartID = layout.GetSubChartID();
    var dataObjID = this.GetRootDataObj().GetObjectID();
    var scm = this.fWSMgr.GetSubChartMgr();
    var subChartID = scm.FindSubChartSubID(dataObjID, parentSubChartID);
    return subChartID;
  }

  HasSubCursor() {
    //this.SetAutoHyper(null);
    return Globals._accountInfo.ReadOnlyMode();
  }

  GetSubCursor(hitTest) {
    var curCell = null;
    var hitElement = hitTest.fElementHit;
    if (hitElement == this) curCell = hitTest.fDisabledElementHit;

    //var curCell = hitTest.fDisabledElementHit;
    if (curCell) {
      var isHyper = curCell.IsHyperLink();
      if (isHyper) {
        //this.SetAutoHyper(curCell);
        return "pointer";
      } else return this.GetCursor();
    }
  }

  GetCursor() {
    var p = this.fParentPanel;
    var curCmd = p.GetBoxCmdCode();
    if (curCmd != null) {
      if (curCmd == "MoveBranch") return "alias";
      else if (curCmd == "AddHolder") {
        if (this.IsPositionBox()) return "copy";
        else return "no-drop";
      } else {
        if (this.CanAddSub()) return "copy";
        else return "no-drop";
      }
    }

    return "default";
  }

  //=================================================================================================
  // Debugging
  ObjectName() {
    return this.fObjectName;
  }

  AddPageLink() {
    var scm = this.fWSMgr.GetDoc().GetSubChartMgr();
    var mySubChartNodes = scm.FindSCSubNodesFromDataObj(this.fDataObj);
    var pages = [];
    for (var i = 0; i < mySubChartNodes.length; i++) {
      var theSubNode = mySubChartNodes[i];
      var SCID = theSubNode.GetSubChartID();
      var theSC = scm.FindSubChart(SCID);
      theSC["pagenum"] = theSC.GetPageNumber();
      pages.push(theSC);
    }
    pages.reverse();
    var offset = 0;
    for (var aPageNum in pages) {
      var theSC = pages[aPageNum];
      var pageString = theSC["pagenum"].toString();
      var PageLink = new QBoxPageLink();
      PageLink.InitPageLink(this, pageString, theSC, offset);
      offset += PageLink.GetWidth() + 6;
      this.PushElement(PageLink);
    }
  }
}
